/**
 * Domain TTS Service
 * Manages progressive background TTS generation for all 8 domains × 3 languages
 * 
 * Flow:
 * 1. After payment → startBatchGeneration(userId, domainReels)
 * 2. Health generates first (synchronously)
 * 3. Other domains generate in background
 * 4. Frontend polls for progress
 */

import { setVerifiedTimelineCache } from '../utils/verifyUserData';

// PRODUCTION URL - Railway Backend
const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

// Domain order for progressive loading
export const DOMAIN_ORDER = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
export const LANGUAGES = ['hinglish', 'hindi', 'english'];

// Domain display config
export const DOMAIN_CONFIG = {
  health: { name: 'Health & Longevity', icon: '💚', color: '#22c55e' },
  career: { name: 'Career & Success', icon: '💼', color: '#3b82f6' },
  wealth: { name: 'Wealth & Prosperity', icon: '💰', color: '#f59e0b' },
  marriage: { name: 'Marriage & Love', icon: '💗', color: '#ec4899' },
  family: { name: 'Family & Relations', icon: '🏡', color: '#a855f7' },
  education: { name: 'Education & Knowledge', icon: '🎓', color: '#6366f1' },
  travel: { name: 'Travel & Adventure', icon: '✈️', color: '#06b6d4' },
  spiritual: { name: 'Spiritual Growth', icon: '🌅', color: '#8b5cf6' },
};

/**
 * Start batch TTS generation for all domains - NOW USES ULTRA-FAST ENDPOINT
 * @param {string} userId - User ID
 * @param {object} domainReels - Domain text data {lang: {domain: text}}
 * @param {string} priorityDomain - Priority domain (ignored in ultra-fast mode)
 * @returns {Promise<object>} - Generation status
 */
export async function startBatchGeneration(userId, domainReels, priorityDomain = 'health') {
  try {
    console.log('🚀 Starting ULTRA-FAST batch TTS generation for user:', userId);
    
    // Convert domainReels from {lang: {domain: text}} to {domain: {lang: text}}
    const domainTexts = {};
    const languages = ['hinglish', 'hindi', 'english'];
    const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
    
    for (const domain of domains) {
      domainTexts[domain] = {};
      for (const lang of languages) {
        // Try both structures
        let text = '';
        if (domainReels[lang] && domainReels[lang][domain]) {
          text = domainReels[lang][domain];
        } else if (domainReels[domain] && domainReels[domain][lang]) {
          text = domainReels[domain][lang];
        }
        
        // Send FULL text - backend handles byte-safe chunking
        if (text && text.length > 20) {
          domainTexts[domain][lang] = text;
        }
      }
    }
    
    // Use ULTRA-FAST endpoint
    const response = await fetch(`${API_URL}/api/domain-tts/ultra-fast-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        domain_texts: domainTexts
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('✅ ULTRA-FAST batch complete:', result.stats?.success_count, 'audio files in', result.stats?.elapsed_seconds, 's');
    
    // Cache audio to IndexedDB - Note: API returns audio_results (snake_case)
    if (result.success && result.audio_results) {
      try {
        const LocalAudioCache = (await import('../utils/localAudioCache')).default;
        for (const [domain, langResults] of Object.entries(result.audio_results)) {
          for (const [lang, audioData] of Object.entries(langResults)) {
            if (audioData.success && audioData.audio_base64) {
              await LocalAudioCache.saveAudioLocal(userId, `domain_${domain}`, lang, audioData.audio_base64);
            }
          }
        }
        console.log('✅ All audio cached to IndexedDB');
      } catch (cacheError) {
        console.log('Cache error (non-fatal):', cacheError);
      }
    }
    
    localStorage.setItem('tts_generation_started', Date.now().toString());
    localStorage.setItem('tts_user_id', userId);
    localStorage.setItem('tts_ultra_fast_complete', 'true');
    
    return { success: true, stats: result.stats, audio_results: result.audio_results };
  } catch (error) {
    console.error('❌ Error in ultra-fast batch:', error);
    throw error;
  }
}

/**
 * Sequential domain-by-domain TTS generation with progress callbacks.
 * Processes one domain at a time: health(0→100%) → career(0→100%) → ...
 * Each domain generates all 3 languages.
 * @param {string} userId
 * @param {object} domainReels - {lang: {domain: text}} or {domain: {lang: text}}
 * @param {function} onDomainProgress - (domainKey, progress 0-100) callback
 * @param {function} onDomainComplete - (domainKey) callback  
 * @returns {Promise<void>}
 */
export async function startSequentialGeneration(userId, domainReels, onDomainProgress, onDomainComplete) {
  const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
  const languages = ['hinglish', 'hindi', 'english'];
  const MAX_RETRIES = 3;
  
  // Prevent duplicate runs — but ALSO expire stale locks (app closed mid-generation)
  const alreadyRunning = localStorage.getItem('tts_currently_generating') === 'true';
  const lockStartTs = Number(localStorage.getItem('tts_lock_start_ts') || '0');
  const lockAge = Date.now() - lockStartTs;
  // Expire lock if > 3 min old (typical TTS generation < 1min, give buffer for slow networks)
  if (alreadyRunning && lockAge < 180000 && lockStartTs > 0) {
    console.log('[startSequentialGeneration] TTS already running (lock fresh), skipping');
    return;
  }
  if (alreadyRunning) {
    console.log('[startSequentialGeneration] Stale lock detected (age=' + Math.round(lockAge/1000) + 's), clearing and resuming');
    // Reset any stuck partial progress (< 100) so UI doesn't show permanent 5% ghost
    try {
      const stuck = JSON.parse(localStorage.getItem('domain_progress') || '{}');
      const ready = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
      const cleaned = {};
      for (const [k, v] of Object.entries(stuck)) {
        if (ready[k] === true || v === 100) cleaned[k] = 100;
        // else: drop stuck partial — will be regenerated
      }
      localStorage.setItem('domain_progress', JSON.stringify(cleaned));
    } catch {}
  }
  
  localStorage.setItem('tts_currently_generating', 'true');
  localStorage.setItem('tts_lock_start_ts', String(Date.now()));
  localStorage.setItem('domain_progress', '{}');
  localStorage.setItem('audio_ready_domains', '{}');
  console.log('Starting STRICT sequential TTS (all 3 languages required per domain)...');
  
  let completedDomains = 0;
  const LocalAudioCache = (await import('../utils/localAudioCache')).default;
  
  // Process domains STRICTLY ONE AT A TIME (matches past-story UX: "Preparing 1 of 8 → 2 of 8 …")
  // and update progress per-language (5% → 33% → 66% → 100%) so each planet ticks up smoothly.
  for (const domain of domains) {
    // Check if ALL 3 languages already cached
    let allLangsCached = true;
    for (const lang of languages) {
      try {
        const has = await LocalAudioCache.hasAudioLocal(userId, `domain_${domain}`, lang);
        if (!has) { allLangsCached = false; break; }
      } catch { allLangsCached = false; break; }
    }
    if (allLangsCached) {
      console.log(`${domain} already fully cached, skipping`);
      completedDomains++;
      const rd = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
      rd[domain] = true;
      localStorage.setItem('audio_ready_domains', JSON.stringify(rd));
      const pd = JSON.parse(localStorage.getItem('domain_progress') || '{}');
      pd[domain] = 100;
      localStorage.setItem('domain_progress', JSON.stringify(pd));
      if (onDomainProgress) onDomainProgress(domain, 100);
      if (onDomainComplete) onDomainComplete(domain);
      continue;
    }
    
    // Build text map for this domain
    const domainTexts = { [domain]: {} };
    for (const lang of languages) {
      let text = '';
      if (domainReels[lang]?.[domain]) text = domainReels[lang][domain];
      else if (domainReels[domain]?.[lang]) text = domainReels[domain][lang];
      if (text && text.length > 20) domainTexts[domain][lang] = text;
    }
    
    const langCount = Object.keys(domainTexts[domain]).length;
    if (langCount === 0) { completedDomains++; continue; }
    
    const progressData = JSON.parse(localStorage.getItem('domain_progress') || '{}');
    progressData[domain] = 5;
    localStorage.setItem('domain_progress', JSON.stringify(progressData));
    if (onDomainProgress) onDomainProgress(domain, 5);
    
    // Retry loop until ALL languages cached or max retries
    const cachedLanguages = new Set();
    let attempt = 0;
    
    while (cachedLanguages.size < langCount && attempt < MAX_RETRIES) {
      attempt++;
      
      // Build retry request with ONLY uncached languages
      const retryTexts = { [domain]: {} };
      for (const [lang, text] of Object.entries(domainTexts[domain])) {
        if (!cachedLanguages.has(lang)) retryTexts[domain][lang] = text;
      }
      
      if (attempt > 1) {
        console.log(`  Retry ${attempt}/${MAX_RETRIES} for ${domain}: ${Object.keys(retryTexts[domain]).length} langs...`);
        await new Promise(r => setTimeout(r, 2000));
      }
      
      try {
        const response = await fetch(`${API_URL}/api/domain-tts/ultra-fast-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, domain_texts: retryTexts })
        });
        
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        
        const audioResults = result.audio_results || {};
        for (const [d, langResults] of Object.entries(audioResults)) {
          for (const [lang, audioData] of Object.entries(langResults || {})) {
            if (audioData.success && audioData.audio_base64 && audioData.audio_base64.length > 5000) {
              await LocalAudioCache.saveAudioLocal(userId, `domain_${d}`, lang, audioData.audio_base64);
              cachedLanguages.add(lang);
              // Smooth per-language progress: 5% → 35% → 65% → 95% → 100% (at completion)
              const pct = Math.min(95, Math.round((cachedLanguages.size / langCount) * 90) + 5);
              progressData[domain] = pct;
              localStorage.setItem('domain_progress', JSON.stringify(progressData));
              if (onDomainProgress) onDomainProgress(domain, pct);
            }
          }
        }
      } catch (e) {
        console.log(`  Attempt ${attempt} failed for ${domain}: ${e.message}`);
      }
    }
    
    completedDomains++;
    const allCached = cachedLanguages.size === langCount;
    
    // Refresh lock timestamp — generation is actively progressing
    localStorage.setItem('tts_lock_start_ts', String(Date.now()));
    
    if (allCached) {
      const rd = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
      rd[domain] = true;
      localStorage.setItem('audio_ready_domains', JSON.stringify(rd));
      progressData[domain] = 100;
      localStorage.setItem('domain_progress', JSON.stringify(progressData));
      if (onDomainProgress) onDomainProgress(domain, 100);
      if (onDomainComplete) onDomainComplete(domain);
      console.log(`[${completedDomains}/8] ${domain} COMPLETE (${langCount}/${langCount} langs)`);
    } else {
      console.log(`[${completedDomains}/8] ${domain} PARTIAL (${cachedLanguages.size}/${langCount} langs) — marking ready anyway so UI progresses`);
      // CRITICAL: even if some languages failed after MAX_RETRIES, mark the domain done
      // (at least one lang is usually cached) so the planet doesn't stay stuck at 5% forever.
      // The user can still play whatever language succeeded; retries happen on next app-open.
      if (cachedLanguages.size > 0) {
        const rd = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
        rd[domain] = true;
        localStorage.setItem('audio_ready_domains', JSON.stringify(rd));
        progressData[domain] = 100;
        localStorage.setItem('domain_progress', JSON.stringify(progressData));
        if (onDomainProgress) onDomainProgress(domain, 100);
        if (onDomainComplete) onDomainComplete(domain);
      } else {
        // Zero languages cached after 3 retries — mark as failed (-1) so UI doesn't loop
        progressData[domain] = -1;
        localStorage.setItem('domain_progress', JSON.stringify(progressData));
      }
    }
  }
  
  localStorage.setItem('tts_generation_started', Date.now().toString());
  localStorage.setItem('tts_user_id', userId);
  localStorage.setItem('tts_ultra_fast_complete', 'true');
  localStorage.removeItem('tts_currently_generating');
  localStorage.removeItem('tts_lock_start_ts');
  console.log(`Sequential TTS done: ${completedDomains}/8 domains`);
}

/**
 * Get current generation progress
 * @param {string} userId - User ID
 * @returns {Promise<object>} - Progress info
 */
export async function getGenerationProgress(userId) {
  try {
    const response = await fetch(`${API_URL}/api/domain-tts/progress/${userId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting progress:', error);
    return null;
  }
}

/**
 * Get detailed domain status
 * @param {string} userId - User ID
 * @returns {Promise<object>} - Domain status
 */
export async function getDomainStatus(userId) {
  // Skip ghost/device IDs: polling with these clutters Railway logs and
  // returns an empty bucket for the wrong user. Callers should use the
  // authenticated user_id only.
  if (!userId || /^(native-|web-|session_|device_|taraanaya_device_)/.test(userId)) {
    return null;
  }
  try {
    const response = await fetch(`${API_URL}/api/domain-tts/status/${userId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting domain status:', error);
    return null;
  }
}

/**
 * Check if a specific domain is ready (all 3 languages)
 * @param {string} userId - User ID
 * @param {string} domain - Domain key
 * @returns {Promise<boolean>}
 */
export async function isDomainReady(userId, domain) {
  try {
    const response = await fetch(`${API_URL}/api/domain-tts/domain-ready/${userId}/${domain}`);
    
    if (!response.ok) {
      return false;
    }
    
    const result = await response.json();
    return result.is_ready === true;
  } catch (error) {
    console.error('Error checking domain ready:', error);
    return false;
  }
}

/**
 * Get pre-generated audio for a domain+language
 * @param {string} userId - User ID
 * @param {string} domain - Domain key
 * @param {string} language - Language code
 * @returns {Promise<object>} - Audio data
 */
export async function getDomainAudio(userId, domain, language) {
  try {
    const response = await fetch(`${API_URL}/api/domain-tts/audio/${userId}/${domain}/${language}`);
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting domain audio:', error);
    return null;
  }
}

/**
 * Poll for generation progress (for UI updates)
 * @param {string} userId - User ID
 * @param {function} onProgress - Callback with progress updates
 * @param {number} interval - Poll interval in ms (default 2000)
 * @returns {function} - Stop polling function
 */
export function pollGenerationProgress(userId, onProgress, interval = 2000) {
  let isPolling = true;
  
  const poll = async () => {
    if (!isPolling) return;
    
    const progress = await getGenerationProgress(userId);
    
    if (progress) {
      onProgress(progress);
      
      // Stop polling if complete
      if (progress.is_complete) {
        console.log('✅ Generation complete, stopping poll');
        isPolling = false;
        return;
      }
    }
    
    // Continue polling
    if (isPolling) {
      setTimeout(poll, interval);
    }
  };
  
  // Start polling
  poll();
  
  // Return stop function
  return () => {
    isPolling = false;
  };
}

/**
 * Initialize TTS generation after payment
 * Should be called when user completes payment
 */
// Global lock to prevent duplicate initializeAfterPayment calls
let _ttsInitLock = false;

export async function initializeAfterPayment() {
  // Prevent duplicate calls (React StrictMode, competing paths)
  if (_ttsInitLock) {
    console.log('[TTS] initializeAfterPayment already running — skipping duplicate call');
    return false;
  }
  _ttsInitLock = true;
  // Lock globally so HexStory/FutureReelsLoading don't fire competing chains
  localStorage.setItem('tts_currently_generating', 'true');
  
  // Clear stale progress from any previous failed/partial runs
  localStorage.setItem('domain_progress', '{}');
  // Clear stale audio ready flags — force fresh progressive loading
  localStorage.setItem('audio_ready_domains', '{}');
  
  try {
    const userId = localStorage.getItem('user_id') || 
                   localStorage.getItem('audio_user_id') || 
                   localStorage.getItem('session_id');
    if (!userId) {
      console.error('No user ID found for TTS generation');
      return false;
    }
    
    localStorage.setItem('tts_user_id', userId);
    
    // Get unified story data with domain_reels
    let storyDataStr = localStorage.getItem('unified_story_data');
    
    // If no story data in localStorage, try to fetch from MongoDB
    if (!storyDataStr) {
      console.log('📡 Fetching unified_story_data from backend...');
      try {
        const response = await fetch(`${API_URL}/api/unified-story/load/${userId}`);
        const result = await response.json();
        if (result.success && result.data) {
          storyDataStr = JSON.stringify(result.data);
          localStorage.setItem('unified_story_data', storyDataStr);
          console.log('✅ Loaded story data from MongoDB');
        }
      } catch (e) {
        console.log('Failed to fetch story data from backend:', e);
      }
    }
    
    if (!storyDataStr) {
      console.log('⚠️ No story data yet — generating unified story first...');
      try {
        const birthRaw = localStorage.getItem('birth_details');
        if (birthRaw) {
          const bd = JSON.parse(birthRaw);
          const birthPayload = {
            user_id: userId,
            name: bd.name || 'User',
            year: parseInt(bd.year), month: parseInt(bd.month), day: parseInt(bd.day),
            hour: parseInt(bd.hour) || 12, minute: parseInt(bd.minute) || 0,
            place: bd.place || 'India', gender: bd.gender || 'unknown',
            latitude: parseFloat(bd.latitude) || 28.6, longitude: parseFloat(bd.longitude) || 77.2
          };
          const genRes = await fetch(`${API_URL}/api/unified-story/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(birthPayload)
          });
          const genResult = await genRes.json();
          if (genResult.success && genResult.data) {
            storyDataStr = JSON.stringify(genResult.data);
            localStorage.setItem('unified_story_data', storyDataStr);
            console.log('✅ Generated unified story on-demand for TTS');
          } else {
            console.log('❌ Failed to generate unified story:', genResult);
            return false;
          }
        } else {
          console.log('❌ No birth details found for story generation');
          return false;
        }
      } catch (e) {
        console.log('❌ Error generating unified story:', e.message);
        return false;
      }
    }
    
    const storyData = JSON.parse(storyDataStr);
    let domainReels = storyData.future_story?.domain_reels || 
                        storyData.story_data?.future_story?.domain_reels;
    
    // If domain_reels don't exist (new user just paid), generate premium content first
    if (!domainReels || Object.keys(domainReels).length === 0) {
      console.log('⚠️ No domain_reels in story data — generating premium content first...');
      try {
        const { generatePremiumContent } = await import('./iapService');
        // Signal that text generation is in progress (HexStory poller reads this)
        localStorage.setItem('future_reels_generating', 'true');
        localStorage.setItem('future_reels_gen_start', String(Date.now()));
        
        const genResult = await generatePremiumContent(['future_reels']);
        if (genResult?.success) {
          // Re-read the updated story data from localStorage (generatePremiumContent writes it)
          const updatedStr = localStorage.getItem('unified_story_data');
          if (updatedStr) {
            const updatedData = JSON.parse(updatedStr);
            domainReels = updatedData.future_story?.domain_reels || updatedData.story_data?.future_story?.domain_reels;
          }
        }
        if (!domainReels || Object.keys(domainReels).length === 0) {
          console.log('❌ Still no domain_reels after premium generation');
          return false;
        }
        console.log('✅ domain_reels generated successfully, proceeding to TTS...');
      } catch (genErr) {
        console.error('Premium content generation failed:', genErr);
        return false;
      }
    }
    
    console.log('Starting SEQUENTIAL domain-by-domain TTS generation');
    localStorage.setItem('tts_generation_started', Date.now().toString());
    
    const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
    const languages = ['hinglish', 'hindi', 'english'];
    const LocalAudioCache = (await import('../utils/localAudioCache')).default;
    let completedDomains = 0;
    const MAX_RETRIES = 3;
    
    // Process ONE DOMAIN AT A TIME — sequential, not parallel
    for (const domain of domains) {
      const domainTexts = {};
      domainTexts[domain] = {};
      
      for (const lang of languages) {
        const text = domainReels[lang]?.[domain] || domainReels[domain]?.[lang] || '';
        if (text && text.length > 20) {
          domainTexts[domain][lang] = text;
        }
      }
      
      const langCount = Object.keys(domainTexts[domain]).length;
      if (langCount === 0) {
        console.log(`Skipping ${domain} — no text available`);
        continue;
      }
      
      console.log(`[${completedDomains + 1}/8] Generating TTS for ${domain} (${langCount} languages)...`);
      
      const progressData = JSON.parse(localStorage.getItem('domain_progress') || '{}');
      progressData[domain] = 5;
      localStorage.setItem('domain_progress', JSON.stringify(progressData));
      
      // Track which languages are cached successfully
      const cachedLanguages = new Set();
      let attempt = 0;
      
      while (cachedLanguages.size < langCount && attempt < MAX_RETRIES) {
        attempt++;
        
        // Build request with ONLY uncached languages
        const retryTexts = { [domain]: {} };
        for (const [lang, text] of Object.entries(domainTexts[domain])) {
          if (!cachedLanguages.has(lang)) {
            retryTexts[domain][lang] = text;
          }
        }
        
        const remainingLangs = Object.keys(retryTexts[domain]).length;
        if (remainingLangs === 0) break;
        
        if (attempt > 1) {
          console.log(`  Retry ${attempt}/${MAX_RETRIES} for ${domain}: ${remainingLangs} languages remaining...`);
          await new Promise(r => setTimeout(r, 2000)); // 2s delay before retry
        }
        
        try {
          const response = await fetch(`${API_URL}/api/domain-tts/ultra-fast-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId, domain_texts: retryTexts })
          });
          
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const result = await response.json();
          
          // Cache audio to IndexedDB — validate quality before saving
          const audioResults = result.audio_results || {};
          for (const [d, langResults] of Object.entries(audioResults)) {
            for (const [lang, audioData] of Object.entries(langResults || {})) {
              if (audioData.success && audioData.audio_base64 && audioData.audio_base64.length > 5000) {
                await LocalAudioCache.saveAudioLocal(
                  userId, `domain_${d}`, lang, audioData.audio_base64,
                  { generated_at: new Date().toISOString(), chars: audioData.chars }
                );
                cachedLanguages.add(lang);
                // Update progress proportionally
                const langProgress = Math.min(95, Math.round((cachedLanguages.size / langCount) * 90) + 5);
                progressData[domain] = langProgress;
                localStorage.setItem('domain_progress', JSON.stringify(progressData));
              } else if (audioData.audio_base64 && audioData.audio_base64.length <= 5000) {
                console.warn(`Audio too small for ${domain}/${lang}: ${audioData.audio_base64.length} chars, skipping`);
              }
            }
          }
          
        } catch (e) {
          console.log(`  Attempt ${attempt} failed for ${domain}: ${e.message}`);
        }
      }
      
      completedDomains++;
      const allCached = cachedLanguages.size === langCount;
      console.log(`[${completedDomains}/8] ${domain}: ${cachedLanguages.size}/${langCount} languages cached${allCached ? ' (COMPLETE)' : ' (PARTIAL)'}`);
      
      // STRICT: Only mark domain as READY when ALL languages are cached
      if (allCached) {
        const readyDomains = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
        readyDomains[domain] = true;
        localStorage.setItem('audio_ready_domains', JSON.stringify(readyDomains));
        progressData[domain] = 100;
        localStorage.setItem('domain_progress', JSON.stringify(progressData));
      } else {
        console.log(`${domain} — only ${cachedLanguages.size}/${langCount} cached, NOT marking ready`);
        progressData[domain] = -1;
        localStorage.setItem('domain_progress', JSON.stringify(progressData));
      }
    }
    
    console.log(`Sequential TTS complete: ${completedDomains}/8 domains`);
    localStorage.setItem('tts_ultra_fast_complete', 'true');
    return completedDomains > 0;
  } catch (error) {
    console.error('Error initializing TTS after payment:', error);
    return false;
  } finally {
    _ttsInitLock = false;
    localStorage.removeItem('tts_currently_generating');
  }
}

/**
 * Check if generation was already started
 * @returns {boolean}
 */
export function isGenerationStarted() {
  const started = localStorage.getItem('tts_generation_started');
  if (!started) return false;
  
  // Consider started if within last 2 hours
  const startTime = parseInt(started);
  const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
  
  return startTime > twoHoursAgo;
}

/**
 * Get generation user ID
 * Uses user_id from login (consistent across sessions) for proper state restoration
 * @returns {string|null}
 */
export function getGenerationUserId() {
  // Priority: logged-in user_id > tts_user_id > audio_user_id > device_id
  // This ensures consistent ID across logout/login and user isolation
  return localStorage.getItem('user_id') || 
         localStorage.getItem('tts_user_id') || 
         localStorage.getItem('audio_user_id') ||
         localStorage.getItem('taraanaya_device_id');
}

/**
 * Clear TTS generation state (call on logout or new user)
 */
export function clearGenerationState() {
  localStorage.removeItem('tts_generation_started');
  localStorage.removeItem('tts_user_id');
  localStorage.removeItem('tts_generation_triggered');
  localStorage.removeItem('tts_ultra_fast_complete');
  localStorage.removeItem('domain_tts_status');
  console.log('🗑️ TTS generation state cleared');
}

/**
 * Pre-load ALL domain insights data for instant Insights/Timeline loading
 * Call this immediately after payment to ensure data is ready
 * @returns {Promise<boolean>}
 */
export async function preloadAllDomainInsights() {
  try {
    console.log('🚀 Pre-loading ALL domain insights for INSTANT access...');
    
    // Get birth details from localStorage
    const birthDetailsStr = localStorage.getItem('birthDetails') || 
                           localStorage.getItem('birth_details') || 
                           localStorage.getItem('birthData');
    
    if (!birthDetailsStr) {
      console.log('⚠️ No birth details found, skipping preload');
      return false;
    }
    
    const birthDetails = JSON.parse(birthDetailsStr);
    const userId = localStorage.getItem('user_id') || localStorage.getItem('session_id') || localStorage.getItem('birth_session_id');
    
    // Normalize birth details
    const normalizedBirth = {
      name: birthDetails.name || birthDetails.userName || 'User',
      year: birthDetails.year || birthDetails.birthYear,
      month: birthDetails.month || birthDetails.birthMonth,
      day: birthDetails.day || birthDetails.birthDay,
      hour: birthDetails.hour || birthDetails.birthHour || 12,
      minute: birthDetails.minute || birthDetails.birthMinute || 0,
      latitude: birthDetails.latitude || birthDetails.lat,
      longitude: birthDetails.longitude || birthDetails.lon,
      gender: birthDetails.gender || 'unknown',
      user_id: userId
    };
    
    // FIRST: Pre-generate unified story ONLY if user has future_reels unlocked
    // (Skip for ₹199 Starter — they only get Life Insights)
    const planUnlocks = JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
    if (planUnlocks.includes('future_reels')) {
      preloadUnifiedStory(normalizedBirth, userId).catch(e => {
        console.log('Unified story preload background:', e.message);
      });
    } else {
      console.log('Skipping unified story preload (plan does not include future_reels)');
    }
    
    // USE FAST BATCH ENDPOINT - Load ALL 8 domains in PARALLEL!
    console.log('⚡ Using FAST batch endpoint for instant loading...');
    const startTime = Date.now();
    
    try {
      const response = await fetch(`${API_URL}/api/domains/all-parallel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizedBirth)
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const allDomainData = result.data;
          let successCount = 0;
          
          // Cache each domain for instant future access (user-stamped)
          Object.entries(allDomainData).forEach(([domain, data]) => {
            if (data && Object.keys(data).length > 0) {
              setVerifiedTimelineCache(`${domain}_timeline_data`, data);
              successCount++;
            }
          });
          
          // Save combined cache for DomainInsights instant load (user-stamped)
          setVerifiedTimelineCache('domain_insights_cache', allDomainData);
          
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
          console.log(`✅ ALL ${successCount}/8 domains pre-loaded in ${elapsed}s (server: ${result.elapsed_seconds}s)`);
          
          return true;
        }
      }
    } catch (batchError) {
      console.log('Batch endpoint failed, falling back to individual loading:', batchError.message);
    }
    
    // FALLBACK: Use batch endpoint if main fails
    console.log('📡 Fallback: Using batch endpoint for all domains...');
    try {
      const response = await fetch(`${API_URL}/api/domains/all-parallel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizedBirth)
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const allDomainData = result.data;
          Object.entries(allDomainData).forEach(([domain, data]) => {
            localStorage.setItem(`${domain}_timeline_data`, JSON.stringify(data));
          });
          localStorage.setItem('domain_insights_cache', JSON.stringify(allDomainData));
          console.log(`✅ Pre-loaded ${result.loaded_count}/8 domains in ${result.elapsed_seconds}s`);
          return true;
        }
      }
    } catch (e) {
      console.log('Batch fallback failed:', e.message);
    }
    
    return false;
  } catch (error) {
    console.error('Error pre-loading domain insights:', error);
    return false;
  }
}

/**
 * Pre-load unified story data for Future Reels
 * This ensures Future Reels load instantly after payment
 */
export async function preloadUnifiedStory(birthDetails, userId) {
  try {
    // Check if already cached
    const existing = localStorage.getItem('unified_story_data');
    if (existing) {
      console.log('✅ Unified story already cached');
      return true;
    }
    
    console.log('📡 Pre-generating unified story for Future Reels...');
    
    // Try to load from MongoDB first
    try {
      const loadResponse = await fetch(`${API_URL}/api/unified-story/load/${userId}`);
      if (loadResponse.ok) {
        const loadResult = await loadResponse.json();
        if (loadResult.success && loadResult.data) {
          localStorage.setItem('unified_story_data', JSON.stringify(loadResult.data));
          console.log('✅ Unified story loaded from MongoDB');
          return true;
        }
      }
    } catch (e) {
      console.log('MongoDB load failed, will generate new:', e.message);
    }
    
    // Generate new unified story
    const generateResponse = await fetch(`${API_URL}/api/unified-story/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...birthDetails,
        user_id: userId
      })
    });
    
    if (generateResponse.ok) {
      const result = await generateResponse.json();
      if (result.success && result.data) {
        localStorage.setItem('unified_story_data', JSON.stringify(result.data));
        console.log('✅ Unified story generated and cached for Future Reels');
        
        // Also save to MongoDB for persistence
        try {
          await fetch(`${API_URL}/api/unified-story/save-story`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId, story_data: result.data })
          });
        } catch (e) {
          console.log('Save to MongoDB failed:', e.message);
        }
        
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error pre-loading unified story:', error);
    return false;
  }
}

export default {
  startBatchGeneration,
  getGenerationProgress,
  getDomainStatus,
  isDomainReady,
  getDomainAudio,
  pollGenerationProgress,
  initializeAfterPayment,
  isGenerationStarted,
  getGenerationUserId,
  clearGenerationState,
  preloadAllDomainInsights,
  preloadUnifiedStory,
  generateAllAudioUltraFast,
  DOMAIN_ORDER,
  LANGUAGES,
  DOMAIN_CONFIG
};

/**
 * 🚀 ULTRA-FAST: Generate ALL 24 audio files (8 domains × 3 languages) in parallel
 * Target: Under 30 seconds total
 * @param {string} userId - User ID
 * @param {object} domainTexts - {domain: {language: text}} for all domains
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<object>} - {success, audioResults, stats}
 */
export async function generateAllAudioUltraFast(userId, domainTexts, onProgress = null) {
  try {
    console.log('🚀 ULTRA-FAST: Starting parallel generation for ALL 24 audio files...');
    const startTime = Date.now();
    
    if (onProgress) onProgress({ status: 'starting', progress: 0, message: 'Initializing audio generation...' });
    
    const response = await fetch(`${API_URL}/api/domain-tts/ultra-fast-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        domain_texts: domainTexts
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    const elapsed = (Date.now() - startTime) / 1000;
    
    console.log(`✅ ULTRA-FAST complete: ${result.stats?.success_count}/24 in ${elapsed.toFixed(1)}s`);
    
    if (onProgress) {
      onProgress({ 
        status: 'complete', 
        progress: 100, 
        message: `Generated ${result.stats?.success_count} audio files in ${elapsed.toFixed(1)}s`,
        stats: result.stats
      });
    }
    
    return result;
  } catch (error) {
    console.error('❌ Ultra-fast generation error:', error);
    if (onProgress) {
      onProgress({ status: 'error', progress: 0, message: error.message });
    }
    throw error;
  }
}
